import React, { useState, useRef } from 'react';
import axios from 'axios';
import {
  Upload, ClipboardList, X, Check, AlertTriangle, Copy, CheckCircle,
  ChevronRight, ChevronLeft, Link2, Send, FileDown,
} from 'lucide-react';
import { downloadLocalFile } from '../lib/download';
import { WhatsAppButton } from './ShareMenu';
import { inviteShareText } from '../lib/share';
import DesktopHint from './DesktopHint';

/**
 * BulkAddMembers — the "Add many" wizard (onboarding push, Aug 2026).
 *
 * Paste a list of emails, or upload CSV/XLSX → (map columns) → preview every
 * row with the server's verdict → send. All validation is server-side
 * (/members/bulk/preview and again inside /send); this component only shapes
 * rows and shows verdicts, so the wizard can never disagree with the API.
 *
 * Children column: names only — they ride the invitation and the parent is
 * walked through adding each child (with DOB) after accepting. No child
 * accounts are created from here.
 */

const FIELDS = [
  { key: 'email', label: 'Email' },
  { key: 'name', label: 'Full name' },
  { key: 'firstName', label: 'First name' },
  { key: 'lastName', label: 'Last name' },
  { key: 'role', label: 'Role' },
  { key: 'teams', label: 'Team(s)' },
  { key: 'children', label: 'Children' },
];

const EMAIL_IN_TEXT = /[^\s<>,;:"'()[\]]+@[^\s<>,;:"'()[\]]+\.[^\s<>,;:"'()[\]]{2,}/;

// One pasted line → a row. Accepts "jamie@x.com", "Jamie Smith <jamie@x.com>",
// "Jamie Smith, jamie@x.com" and plain names (no email ⇒ share-a-link row).
function rowFromLine(line) {
  const m = line.match(EMAIL_IN_TEXT);
  const email = m ? m[0] : '';
  const name = line
    .replace(email, '')
    .replace(/[<>]/g, ' ')
    .replace(/[,;|\t]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  return { email, firstName: name, lastName: '' };
}

const STATUS_STYLE = {
  ok: { color: '#2D7A4F' },
  error: { color: '#A03A2E' },
};

// Template the club fills in with Excel / Google Sheets. Headers match
// memberBulk.js HEADER_HINTS so the column map is auto-suggested on upload;
// example rows use the club's real team names where we have them, and cover
// the interesting cases (parent with children; no email ⇒ share-a-link row).
function buildTemplateCsv(teams) {
  const t1 = teams[0]?.name || 'U11s';
  const t2 = teams[1]?.name || teams[0]?.name || '1st Team';
  const esc = (v) => (/[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v);
  const rows = [
    ['Email', 'First name', 'Last name', 'Role', 'Teams', 'Children'],
    ['jamie@example.com', 'Jamie', 'Smith', 'Player', t1, ''],
    ['priya@example.com', 'Priya', 'Patel', 'Parent', t1, 'George Patel; Amy Patel'],
    ['dan@example.com', 'Dan', 'Hughes', 'Team Manager', t2, ''],
    ['', 'Sam', 'Taylor', 'Coach', t2, ''],
  ];
  return rows.map(r => r.map(esc).join(',')).join('\r\n') + '\r\n';
}

export default function BulkAddMembers({ clubSlug, clubName, teams = [], isClubAdmin, accentColor, onClose }) {
  const [step, setStep] = useState('input'); // input | map | preview | results
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  // input
  const [pasteText, setPasteText] = useState('');
  const fileRef = useRef(null);

  // parsed file
  const [grid, setGrid] = useState(null); // { header, rows, truncated, maxRows }
  const [mapping, setMapping] = useState({});

  // defaults
  const [defaultRole, setDefaultRole] = useState('player');
  const [defaultTeamId, setDefaultTeamId] = useState('');

  // preview + send
  const [rows, setRows] = useState([]);        // raw rows sent to the API
  const [verdicts, setVerdicts] = useState(null);
  const [excluded, setExcluded] = useState(new Set());
  const [sendResult, setSendResult] = useState(null);
  const [copiedIdx, setCopiedIdx] = useState(null);

  const base = `/clubs/${clubSlug}/members/bulk`;
  const defaults = { role: defaultRole, teamIds: defaultTeamId ? [defaultTeamId] : [] };

  const roleOptions = isClubAdmin
    ? ['player', 'coach', 'team_manager', 'parent', 'supporter', 'admin']
    : ['player', 'coach'];
  const ROLE_LABELS = {
    player: 'Player', coach: 'Coach', team_manager: 'Team manager',
    parent: 'Parent / Guardian', supporter: 'Supporter', admin: 'Club admin',
  };

  const fail = (err, fallback) => setError(err?.response?.data?.error || err?.message || fallback);

  // ── Step 1 → preview (paste path) ──────────────────────────────────
  const previewRows = async (nextRows) => {
    setBusy(true); setError(null);
    try {
      const res = await axios.post(`${base}/preview`, { rows: nextRows, defaults });
      setRows(nextRows);
      setVerdicts(res.data);
      setExcluded(new Set());
      setStep('preview');
    } catch (err) { fail(err, 'Could not check that list.'); }
    finally { setBusy(false); }
  };

  // W-6: accept a "looks like you meant X?" team chip — rewrite that row's
  // team text to the real team name and re-run the server preview, so the
  // verdict (and what gets sent) always comes from the API, never the client.
  const applyTeamSuggestion = (i, sug) => {
    const replacePart = (text) => String(text || '')
      .split(/([;,])/)
      .map(part => (part.trim().toLowerCase() === String(sug.typed).trim().toLowerCase() ? sug.teamName : part))
      .join('');
    const next = rows.map((r, ri) => (ri === i ? { ...r, teams: replacePart(r.teams) } : r));
    previewRows(next);
  };

  const handlePaste = () => {
    const lines = pasteText.split('\n').map(l => l.trim()).filter(Boolean);
    if (!lines.length) { setError('Paste at least one line'); return; }
    previewRows(lines.map(rowFromLine));
  };

  // ── Step 1 → map (file path) ───────────────────────────────────────
  const handleFile = async (file) => {
    if (!file) return;
    setBusy(true); setError(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${base}/parse`, fd);
      setGrid(res.data);
      setMapping(res.data.suggestedMapping || {});
      setStep('map');
    } catch (err) { fail(err, 'Could not read that file.'); }
    finally { setBusy(false); }
  };

  const applyMapping = () => {
    if (mapping.email === undefined && mapping.name === undefined && mapping.firstName === undefined) {
      setError('Point at least the Email column (or a Name column for link-only invites)');
      return;
    }
    const pick = (row, key) => (mapping[key] !== undefined ? String(row[mapping[key]] ?? '') : '');
    const mapped = grid.rows.map(r => ({
      email: pick(r, 'email'),
      // "Full name" wins over first/last when both are mapped.
      firstName: pick(r, 'name') || pick(r, 'firstName'),
      lastName: pick(r, 'name') ? '' : pick(r, 'lastName'),
      role: pick(r, 'role'),
      teams: pick(r, 'teams'),
      children: pick(r, 'children'),
    }));
    previewRows(mapped);
  };

  // ── Preview → send ─────────────────────────────────────────────────
  const handleSend = async () => {
    setBusy(true); setError(null);
    try {
      // Send only rows the preview approved and the user kept — the server
      // re-validates anyway, but this keeps "Invite N people" an exact promise
      // (verdicts.rows is index-aligned with rows; the server never filters).
      const included = rows.filter((_, i) => verdicts?.rows[i]?.status === 'ok' && !excluded.has(i));
      const res = await axios.post(`${base}/send`, { rows: included, defaults });
      setSendResult(res.data);
      setStep('results');
    } catch (err) { fail(err, 'Could not send the invites.'); }
    finally { setBusy(false); }
  };

  const copyLink = (url, idx) => {
    navigator.clipboard?.writeText(url).then(() => {
      setCopiedIdx(idx);
      setTimeout(() => setCopiedIdx(null), 1500);
    });
  };

  const okCount = verdicts ? verdicts.rows.filter((v, i) => v.status === 'ok' && !excluded.has(i)).length : 0;

  const defaultsBar = (
    <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', padding: '10px 12px', borderRadius: 8, background: 'var(--cream, #F4EFE6)', marginBottom: 12 }}>
      <div className="form-group" style={{ margin: 0, minWidth: 150 }}>
        <label className="label">Role if not in the list</label>
        <select className="input input-sm" value={defaultRole} onChange={e => setDefaultRole(e.target.value)}>
          {roleOptions.map(r => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}
        </select>
      </div>
      <div className="form-group" style={{ margin: 0, minWidth: 150 }}>
        <label className="label">Team if not in the list</label>
        <select className="input input-sm" value={defaultTeamId} onChange={e => setDefaultTeamId(e.target.value)}>
          <option value="">No default team</option>
          {teams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
      </div>
    </div>
  );

  return (
    <div className="modal-overlay" onClick={() => onClose(step === 'results')}>
      <div className="modal-card modal-md" style={{ maxWidth: 720 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Add many members</h3>
          <button className="btn btn-ghost btn-icon" onClick={() => onClose(step === 'results')}><X size={18} /></button>
        </div>

        {/* ── INPUT ─────────────────────────────────────────────── */}
        {step === 'input' && (
          <>
            <DesktopHint />
            <p className="modal-subtitle">
              Paste a list of people, or upload a spreadsheet. Everyone gets a normal
              club invite — they appear under Pending invites until they accept.
            </p>
            {defaultsBar}
            <div className="form-group">
              <label className="label"><ClipboardList size={13} /> Paste a list</label>
              <textarea
                className="input" rows={6}
                placeholder={'One person per line — any of these work:\njamie@example.com\nJamie Smith <jamie@example.com>\nJamie Smith  (no email — you get a link to share)'}
                value={pasteText} onChange={e => setPasteText(e.target.value)}
                style={{ fontFamily: 'inherit', resize: 'vertical' }}
              />
              <button className="btn btn-primary" style={{ background: accentColor, marginTop: 8 }}
                disabled={busy || !pasteText.trim()} onClick={handlePaste}>
                {busy ? 'Checking…' : <>Check the list <ChevronRight size={14} /></>}
              </button>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '6px 0 10px', color: 'var(--text-secondary)', fontSize: 13 }}>
              <div style={{ flex: 1, height: 1, background: 'var(--border, #E8DFCE)' }} />
              or
              <div style={{ flex: 1, height: 1, background: 'var(--border, #E8DFCE)' }} />
            </div>
            <div className="form-group">
              <input ref={fileRef} type="file" accept=".csv,.xlsx,.tsv,.txt" style={{ display: 'none' }}
                onChange={e => handleFile(e.target.files?.[0])} />
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <button className="btn btn-secondary" disabled={busy} onClick={() => fileRef.current?.click()}>
                  <Upload size={14} /> {busy ? 'Reading…' : 'Upload CSV or Excel'}
                </button>
                <button className="btn btn-ghost" disabled={busy}
                  onClick={() => downloadLocalFile('marakin-members-template.csv', buildTemplateCsv(teams)).catch(err => fail(err, 'Could not save the template'))}>
                  <FileDown size={14} /> Download the template
                </button>
              </div>
              <p style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 6 }}>
                Not sure of the format? The template opens in Excel or Google Sheets with the
                columns ready — Email, First name, Last name, Role, Teams, Children — and a few
                example people showing how each one works (replace them with your own).
                Leave Email blank for anyone without one and you'll get a link to share instead.
                Up to 500 people per batch.
              </p>
            </div>
          </>
        )}

        {/* ── MAP COLUMNS ───────────────────────────────────────── */}
        {step === 'map' && grid && (
          <>
            <p className="modal-subtitle">
              Tell us what each column is. {grid.rowCount} row{grid.rowCount === 1 ? '' : 's'} found
              {grid.truncated ? ` (only the first ${grid.maxRows} will be used)` : ''}.
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))', gap: 10, marginBottom: 12 }}>
              {FIELDS.map(f => (
                <div className="form-group" key={f.key} style={{ margin: 0 }}>
                  <label className="label">{f.label}</label>
                  <select className="input input-sm" value={mapping[f.key] ?? ''}
                    onChange={e => setMapping(m => ({ ...m, [f.key]: e.target.value === '' ? undefined : Number(e.target.value) }))}>
                    <option value="">—</option>
                    {grid.header.map((h, i) => <option key={i} value={i}>{h}</option>)}
                  </select>
                </div>
              ))}
            </div>
            <div style={{ overflowX: 'auto', border: '1px solid var(--border, #E8DFCE)', borderRadius: 8, marginBottom: 12 }}>
              <table style={{ borderCollapse: 'collapse', fontSize: 12, minWidth: '100%' }}>
                <thead><tr>{grid.header.map((h, i) => (
                  <th key={i} style={{ padding: '6px 10px', textAlign: 'left', whiteSpace: 'nowrap', borderBottom: '1px solid var(--border, #E8DFCE)' }}>{h}</th>
                ))}</tr></thead>
                <tbody>
                  {grid.rows.slice(0, 4).map((r, i) => (
                    <tr key={i}>{grid.header.map((_, c) => (
                      <td key={c} style={{ padding: '5px 10px', whiteSpace: 'nowrap', color: 'var(--text-secondary)' }}>{String(r[c] ?? '')}</td>
                    ))}</tr>
                  ))}
                </tbody>
              </table>
            </div>
            {defaultsBar}
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn btn-ghost" onClick={() => { setGrid(null); setStep('input'); }}>
                <ChevronLeft size={14} /> Back
              </button>
              <button className="btn btn-primary" style={{ background: accentColor }} disabled={busy} onClick={applyMapping}>
                {busy ? 'Checking…' : <>Check the list <ChevronRight size={14} /></>}
              </button>
            </div>
          </>
        )}

        {/* ── PREVIEW ───────────────────────────────────────────── */}
        {step === 'preview' && verdicts && (
          <>
            <p className="modal-subtitle">
              <strong>{okCount}</strong> will be invited
              {verdicts.errors > 0 && <> · <span style={STATUS_STYLE.error}>{verdicts.errors} can't be sent</span> (they're skipped — the reason is on each row)</>}.
            </p>
            <div style={{ maxHeight: 340, overflowY: 'auto', border: '1px solid var(--border, #E8DFCE)', borderRadius: 8, marginBottom: 12 }}>
              {verdicts.rows.map((v, i) => (
                <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', padding: '8px 10px', borderBottom: '1px solid var(--border, #F0EAD9)', opacity: excluded.has(i) ? 0.45 : 1 }}>
                  {v.status === 'ok'
                    ? <Check size={15} style={{ ...STATUS_STYLE.ok, flexShrink: 0, marginTop: 2 }} />
                    : <AlertTriangle size={15} style={{ ...STATUS_STYLE.error, flexShrink: 0, marginTop: 2 }} />}
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {[v.firstName, v.lastName].filter(Boolean).join(' ') || v.email || '(row ' + (i + 1) + ')'}
                      {v.email && <span style={{ fontWeight: 400, color: 'var(--text-secondary)' }}> · {v.email}</span>}
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                      {v.status === 'ok' && <>{v.describe}{v.childCount ? ` · ${v.childCount} child${v.childCount > 1 ? 'ren' : ''}` : ''}</>}
                      {v.notes.map((n, ni) => (
                        <span key={ni} style={v.status === 'error' ? STATUS_STYLE.error : undefined}>
                          {v.status === 'ok' ? ' · ' : ni > 0 ? ' · ' : ''}{n}
                        </span>
                      ))}
                    </div>
                    {Array.isArray(v.teamSuggestions) && v.teamSuggestions.length > 0 && (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 6 }}>
                        {v.teamSuggestions.map((sug, si) => (
                          <button key={si} type="button" className="btn btn-secondary btn-sm" disabled={busy}
                            onClick={() => applyTeamSuggestion(i, sug)}>
                            Use "{sug.teamName}"
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  {v.status === 'ok' && (
                    <button className="btn btn-ghost btn-sm" style={{ flexShrink: 0 }}
                      onClick={() => setExcluded(prev => { const n = new Set(prev); n.has(i) ? n.delete(i) : n.add(i); return n; })}>
                      {excluded.has(i) ? 'Include' : 'Remove'}
                    </button>
                  )}
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn btn-ghost" onClick={() => setStep(grid ? 'map' : 'input')}>
                <ChevronLeft size={14} /> Back
              </button>
              <button className="btn btn-primary" style={{ background: accentColor }} disabled={busy || okCount === 0} onClick={handleSend}>
                {busy ? 'Sending…' : <><Send size={14} /> Invite {okCount} {okCount === 1 ? 'person' : 'people'}</>}
              </button>
            </div>
          </>
        )}

        {/* ── RESULTS ───────────────────────────────────────────── */}
        {step === 'results' && sendResult && (
          <>
            <p className="modal-subtitle">
              <Check size={14} style={STATUS_STYLE.ok} /> <strong>{sendResult.sent}</strong> invite{sendResult.sent === 1 ? '' : 's'} sent by email
              {sendResult.links > 0 && <> · <strong>{sendResult.links}</strong> link{sendResult.links === 1 ? '' : 's'} to share yourself</>}
              {sendResult.skipped > 0 && <> · {sendResult.skipped} skipped</>}.
              Invites expire in 7 days — they're all under Pending invites with Resend and Revoke.
            </p>
            {sendResult.results.filter(r => r.status === 'link').length > 0 && (
              <div style={{ marginBottom: 12 }}>
                <label className="label"><Link2 size={13} /> Share these yourself (no email address)</label>
                <div style={{ maxHeight: 200, overflowY: 'auto' }}>
                  {sendResult.results.filter(r => r.status === 'link').map((r, i) => (
                    <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 6 }}>
                      <span style={{ fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap' }}>{r.name || 'Invite'}</span>
                      <input className="input input-sm" readOnly value={r.inviteUrl} style={{ flex: 1, fontSize: 12 }} />
                      <button className="btn btn-secondary btn-sm" onClick={() => copyLink(r.inviteUrl, i)}>
                        {copiedIdx === i ? <><CheckCircle size={13} /> Copied</> : <><Copy size={13} /> Copy</>}
                      </button>
                      <WhatsAppButton
                        text={inviteShareText(clubName, r.inviteUrl, (r.name || '').split(' ')[0])}
                        clubSlug={clubSlug} className="btn btn-secondary btn-sm" size={13}
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}
            {sendResult.results.filter(r => r.status === 'skipped').length > 0 && (
              <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 12 }}>
                Skipped: {sendResult.results.filter(r => r.status === 'skipped').map(r => r.email || 'unnamed row').join(', ')}
              </div>
            )}
            <button className="btn btn-primary" style={{ background: accentColor }} onClick={() => onClose(true)}>
              Done
            </button>
          </>
        )}

        {error && <div className="invite-error" style={{ marginTop: 10 }}><AlertTriangle size={14} /> {error}</div>}
      </div>
    </div>
  );
}
